import { css, html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import '../library/editors/elements/d-edit-text.js';
import '../library/elements/d-smooth-resize.js';
import '../library/lists/d-list-section.js';
import { BaseDialog } from 'src/library/components/BaseDialog.js';
import { t } from 'src/i18n';
import type { DialogActionInput } from '../library/components/DialogActionInput';
import type { ListSectionItemInput } from 'src/library/lists/listSectionItemInput';
import type { AddRelatedEntityDialogInput } from 'src/content/add-related-entity-dialog-input';
import type { AddRelatedEntityDialogResult } from 'src/content/add-related-entity-dialog-result';

@customElement('add-related-entity-dialog')
export class AddRelatedEntityDialog extends BaseDialog<AddRelatedEntityDialogInput, AddRelatedEntityDialogResult> {
  static readonly styles = BaseDialog.styles.concat(css`
    .post-header {
      display: flex;
      flex-wrap: wrap;
      gap: 12px;
      align-items: baseline;
      margin: 0 20px;
      padding: 12px 0;
    }
    d-edit-text {
      flex-grow: 1;
      min-width: 300px;
    }
    .body {
      max-height: 400px;
    }
  `);
  @property({ type: Boolean })
  show = false;
  @property({ type: String })
  entityType = '';
  @property({ type: Array })
  availableEntities: ListSectionItemInput[] = [];
  @property({ type: Array })
  selectedEntities: string[] = [];
  @property({ type: String })
  searchString = '';
  width = 600;

  protected get calculatedTitle(): string {
    if (this.entityType === 'eventOccurrences') {
      return t('content.addRelatedFindTask');
    }
    if (this.entityType === 'tasks') {
      return t('content.addRelatedFindRoutine');
    }
    return this.title;
  }

  protected get calculatedFooterActions(): DialogActionInput[] {
    return [
      { name: t('content.cancel'), action: 'cancel' },
      { name: t('content.addRelatedAdd'), action: 'done', main: true, disabled: this.selectedEntities.length === 0 },
    ];
  }

  private get placeholder() {
    if (this.entityType === 'eventOccurrences') {
      return t('content.addRelatedSearchTasks');
    }
    if (this.entityType === 'tasks') {
      return t('content.addRelatedSearchRoutines');
    }
    return t('content.addRelatedSearch');
  }

  private selectEntity(uuid: string) {
    const selectedEntities: string[] = [...this.selectedEntities, uuid];
    this.selectedEntities = selectedEntities;
  }

  private unSelectEntity(uuid: string) {
    const selectedEntities: string[] = this.selectedEntities.filter((e) => e !== uuid);
    this.selectedEntities = selectedEntities;
  }

  private get searchResults(): ListSectionItemInput[] {
    if (this.searchString.length > 1) {
      return this.availableEntities
        .filter((e) => {
          return (
            e.label &&
            e.label.toLowerCase().includes(this.searchString.toLowerCase()) &&
            !this.selectedEntities.includes(e.clickData ?? '')
          );
        })
        .map((e) => {
          return { ...e, clickHandler: () => this.selectEntity(e.clickData ?? '') };
        });
    }
    return [];
  }

  private get selectedListItems(): ListSectionItemInput[] {
    return this.availableEntities
      .filter((e) => e.clickData && this.selectedEntities.includes(e.clickData))
      .map((e) => {
        return { ...e, clickHandler: () => this.unSelectEntity(e.clickData ?? '') };
      });
  }

  protected renderPostHeader() {
    return html`
      <div>
        <div class="post-header">
          <d-edit-text
            type="search"
            system-content
            theme-page
            .placeholder=${this.placeholder}
            .value=${this.searchString}
            @value-changed=${(e: CustomEvent<{ value: string }>) => {
              e.stopPropagation();
              this.searchString = e.detail.value;
            }}
          ></d-edit-text>
        </div>
      </div>
    `;
  }
  renderBody() {
    return html`
      <d-smooth-resize>
        ${this.searchString.length > 1
          ? html`
              <d-list-section
                no-header
                bordered
                .items=${this.searchResults}
                no-item-text=${t('content.addRelatedNoResults')}
              ></d-list-section>
            `
          : nothing}
      </d-smooth-resize>
    `;
  }

  protected renderFooter() {
    return html`
      <div class="footer-wrapper">
        <d-smooth-resize>
          ${this.selectedListItems.length > 0
            ? html`
                <d-list-section
                  .icon=${this.entityType}
                  label=${t('content.addRelatedAddedHeader')}
                  sublabel=${t('content.addRelatedTapToRemove')}
                  bordered
                  .items=${this.selectedListItems}
                ></d-list-section>
              `
            : nothing}
        </d-smooth-resize>
        ${this.calculatedFooterActions.length > 0
          ? html` <div class="footer-actions">${this.renderFooterActions()}</div> `
          : nothing}
      </div>
    `;
  }

  protected fetchResult(detail: string | undefined): AddRelatedEntityDialogResult {
    if (detail === 'done') {
      return {
        action: 'done',
        selectedEntities: this.selectedEntities,
      };
    } else {
      return {
        action: 'cancel',
      };
    }
  }

  protected initializeDialog(input: AddRelatedEntityDialogInput) {
    this.entityType = input.entityType;
    this.availableEntities = input.availableEntities;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'add-related-entity-dialog': AddRelatedEntityDialog;
  }
}
